/**
 * Helpers de apresentação compartilhados do módulo de Licitações.
 *
 * Extraídos do `LicitacoesTab.tsx` na Onda 3, quando a tabela do Encontrar
 * saiu para componente próprio e precisou dos mesmos utilitários. A
 * alternativa seria o filho importar do pai — import circular, que funciona
 * até o dia em que não funciona.
 *
 * `PORTAL_MAP` morar aqui também encosta no F-5 da auditoria de 13/08: havia
 * QUATRO cópias dele no front (LicitacoesTab, PipelineView, MonitoringView e
 * DisputeView), divergindo entre si — portal presente numa cópia e ausente na
 * outra aparecia com badge numa tela e com o slug cru na vizinha. Esta passa a
 * ser a fonte do LicitacoesTab e da tabela do Encontrar; as outras duas seguem
 * como estavam, para quando o backend servir o registry.
 */

import { comprasnetPublicUrl, pncpPublicUrl } from '../../utils/procurementEnums';

/**
 * `color` é o SELO do status. Padrão da Central (L2, 23/10/2026): status "bom" ou de andamento é NEUTRO, como o "Ativo"
 * das listas; cor fica só no que é problema ou pede ação (Suspensa em âmbar; Revogada e Fracassada em vermelho).
 * `dotColor` é outra coisa: é a identidade da coluna no Pipeline (K2: a cor de categoria fica no ponto), e não muda.
 */
export const ITEM_STATUSES = [
  // Triagem (Encontrar)
  { value: 'novo', label: 'Aberto', color: 'bg-slate-600/30 text-slate-300', dotColor: 'bg-green-400' },
  { value: 'analisando', label: 'Analisando', color: 'bg-slate-600/30 text-slate-300', dotColor: 'bg-yellow-400' },
  { value: 'interessado', label: 'Interessado', color: 'bg-slate-600/30 text-slate-300', dotColor: 'bg-cyan-400' },
  { value: 'descartado', label: 'Descartado', color: 'bg-slate-500/20 text-slate-400', dotColor: 'bg-slate-400' },
  // Participação (Cadastrar/Disputar)
  { value: 'participando', label: 'Participando', color: 'bg-slate-600/30 text-slate-300', dotColor: 'bg-orange-400' },
  { value: 'aguardando_julgamento', label: 'Aguardando julgamento', color: 'bg-slate-600/30 text-slate-300', dotColor: 'bg-indigo-400' },
  { value: 'suspensa', label: 'Suspensa', color: 'bg-amber-500/20 text-amber-400', dotColor: 'bg-amber-400' },
  { value: 'desistida', label: 'Desistida', color: 'bg-slate-600/30 text-slate-300', dotColor: 'bg-rose-400' },
  // Terminais (do portal)
  { value: 'homologado', label: 'Homologado', color: 'bg-slate-600/30 text-slate-300', dotColor: 'bg-emerald-400' },
  { value: 'encerrada', label: 'Encerrada', color: 'bg-slate-600/30 text-slate-300', dotColor: 'bg-slate-400' },
  { value: 'revogada', label: 'Revogada', color: 'bg-red-500/20 text-red-400', dotColor: 'bg-red-400' },
  { value: 'fracassada', label: 'Fracassada', color: 'bg-red-700/20 text-red-300', dotColor: 'bg-red-500' },
];

/**
 * Portais em que o HUB tem AUTOMAÇÃO de verdade — scrape, envio de proposta e
 * disputa. Trocar o portal para um destes muda o comportamento do robô; para
 * qualquer outra das ~39 chaves do PORTAL_MAP a troca é só rótulo de origem.
 *
 * Essa distinção não existia na tela, e era a causa da confusão no seletor: o
 * "✓" dizia "tem credencial em Acessos" e era lido como "tem automação". São
 * coisas independentes — dá para ter login sem robô e robô sem login.
 *
 * ⚠️ Ao ligar automação para um portal novo, acrescente a chave AQUI. Quem
 * define a lista é o backend (comprasnet via extensão, bll/bnc via gkz, pcp via
 * pcp-auth); esta constante é o espelho para a UI.
 */
export const PORTAIS_COM_AUTOMACAO = new Set<string>(['comprasnet', 'bll', 'bnc', 'pcp']);

export const PORTAL_MAP: Record<string, { label: string; color: string }> = {
  comprasnet: { label: 'Compras.gov.br', color: 'text-blue-400' },
  comprasnet_estadual: { label: 'ComprasNet Estadual', color: 'text-slate-400' }, // comprasnet.<UF>.gov.br (ex. Sergipe) — portal estadual próprio, SEM automação (≠ ComprasNet federal)
  bnc: { label: 'BNC', color: 'text-emerald-400' },
  bll: { label: 'BLL', color: 'text-purple-400' },
  pcp: { label: 'Portal Compras Públicas', color: 'text-cyan-400' },
  caixa: { label: 'Caixa', color: 'text-orange-400' },
  licitanet: { label: 'Licitanet', color: 'text-teal-400' },
  licitar_digital: { label: 'Licitar Digital', color: 'text-teal-400' },
  amm_licita: { label: 'AMM Licita', color: 'text-teal-400' },       // white-label da Licitar Digital (Assoc. Mineira de Municípios, app2.ammlicita.org.br)
  licitacon: { label: 'LicitaCon', color: 'text-teal-400' },
  publinexo: { label: 'Publinexo', color: 'text-teal-400' },
  banrisul: { label: 'Banrisul', color: 'text-teal-400' },
  compras_mg: { label: 'Compras MG', color: 'text-indigo-400' },
  procergs: { label: 'Procergs', color: 'text-indigo-400' },
  compras_pr: { label: 'Compras PR', color: 'text-indigo-400' },
  ipm: { label: 'IPM', color: 'text-slate-400' },
  megasoft: { label: 'Megasoft', color: 'text-slate-400' },
  fiorilli: { label: 'Fiorilli', color: 'text-slate-400' },
  conam: { label: 'CONAM', color: 'text-slate-400' },
  betha: { label: 'Betha', color: 'text-slate-400' },
  elotech: { label: 'Elotech', color: 'text-slate-400' },
  digifred: { label: 'Digifred', color: 'text-slate-400' },
  governancabrasil: { label: 'Gov. Brasil', color: 'text-slate-400' },
  sislog_go: { label: 'SISLOG Goias', color: 'text-lime-400' },
  bbmnet: { label: 'BBMNET', color: 'text-teal-400' },
  comprasbr: { label: 'ComprasBR', color: 'text-teal-400' },
  licita_mais: { label: 'Licita Mais Brasil', color: 'text-teal-400' },
  licitapp: { label: 'LicitApp', color: 'text-teal-400' },
  pe_integrado: { label: 'Pernambuco Integrado', color: 'text-indigo-400' },
  senac: { label: 'SENAC', color: 'text-rose-400' },
  senai: { label: 'SENAI CETIQT', color: 'text-rose-400' },
  sest_senat: { label: 'SEST SENAT', color: 'text-rose-400' },
  fiergs: { label: 'Sistema FIERGS', color: 'text-rose-400' },
  fieb: { label: 'Sistema FIEB', color: 'text-rose-400' },
  slix: { label: 'SLIX', color: 'text-teal-400' },
  siades_es: { label: 'SIADES Espirito Santo', color: 'text-indigo-400' },
  sgc_ms: { label: 'SGC MS', color: 'text-indigo-400' },
  siga_rj: { label: 'SIGA Rio de Janeiro', color: 'text-indigo-400' },
  siga_ms: { label: 'SIGA MS', color: 'text-indigo-400' },
  daae_rio_claro: { label: 'DAAE Rio Claro', color: 'text-teal-400' },
  br_conectado: { label: 'BR Conectado', color: 'text-amber-400' },
  ec: { label: 'e-Compras Amazonas', color: 'text-indigo-400' }, // sourceAggregator do AL p/ Portal e-Compras AM
  portal_compras_ibate: { label: 'Portal Compras Ibate - SP', color: 'text-teal-400' },
  portal_daae_araraquara: { label: 'PORTAL DE LICITAÇÕES DAAE ARARAQUARA', color: 'text-teal-400' },
  camara_jau: { label: 'Camara Municipal de Jau - SP', color: 'text-teal-400' },
  compras_amazonas: { label: 'Compras Amazonas', color: 'text-indigo-400' },
  compras_df: { label: 'Compras DF', color: 'text-indigo-400' },
  compras_ceara: { label: 'Compras Governo Ceara', color: 'text-indigo-400' },
  compras_jundiai: { label: 'Compras Jundiai', color: 'text-indigo-400' },
  compras_mt: { label: 'Compras Mato Grosso', color: 'text-indigo-400' },
  compras_rs: { label: 'Compras Rio Grande do Sul', color: 'text-indigo-400' },
  compras_sc: { label: 'Compras Santa Catarina', color: 'text-indigo-400' },
  compras_siga_ma: { label: 'Compras SIGA Maranhao', color: 'text-indigo-400' },
  senac_pr: { label: 'SENAC PR', color: 'text-rose-400' },
  senac_sp: { label: 'SENAC SP', color: 'text-rose-400' },
  licitacoes_e_bb: { label: 'Licitacoes-e BB', color: 'text-yellow-400' },
  bb: { label: 'Licitacoes-e BB', color: 'text-yellow-400' },       // alias AlertaLicitação (BB / licitacoes-e). BBMNET é outro portal (já mapeado acima).
  pncp: { label: 'PNCP', color: 'text-slate-500' },
  // Portais agregados via AlertaLicitação (prefixos id_licitacao)
  //
  // Prefixo que o AL não mapeia cai em `prefix.toLowerCase()` no ingest e
  // chegava aqui como slug cru: a linha mostrava "avndv", "gtln", "jrml". São
  // murais e diários municipais — o aviso existe, o portal de disputa não.
  // Nomeá-los é o que permite ao operador ver de relance que aquela linha não
  // tem prazo verificável (auditoria de 03/09/2026).
  //
  // ⚠️ Rótulo GENÉRICO, nunca o nome de um município. A primeira versão disto
  // batizou cada slug pela cidade em que ele foi visto (`trp` virou
  // "Transparência (RJ)") e a validação em localhost mostrou o mesmo `trp` numa
  // licitação de Bataguassu/MS: são FAMÍLIAS de portal, não municípios. Slug de
  // município específico não entra aqui — a regra de forma do `isMuralSource`
  // já os rotula como Mural/Diário sem precisar de manutenção.
  dm: { label: '📰 Diário Municipal', color: 'text-violet-400' },
  dmsc: { label: '📰 Diário Municipal (SC)', color: 'text-violet-400' },
  trp: { label: '📰 Portal de Transparência', color: 'text-violet-400' },
  pb: { label: '📰 Mural TCE-PB', color: 'text-violet-400' },
  dou: { label: 'DOU', color: 'text-amber-300' },
  mural_negocial: { label: 'Mural Negocial', color: 'text-violet-400' },
  tce: { label: 'TCE', color: 'text-emerald-300' },
  mgsf: { label: 'MGSF', color: 'text-slate-400' },
  ascp: { label: 'ASCP', color: 'text-slate-400' },
  srr: { label: 'SRR', color: 'text-slate-400' },
  // ⛔ 15/09/2026: aqui existia `compras_br`, apontando para o MESMO rótulo do
  // `comprasbr` logo acima. Eram duas chaves para um portal só, e foi
  // justamente esse rótulo repetido que escondeu o defeito por meses: o
  // backend gravava as duas grafias (prefixo do AlertaLicitação numa,
  // host do PNCP na outra) e a tela mostrava "ComprasBR" nos dois casos.
  // O resultado visível era o alerta de duplicata dizendo que a licitação
  // estava "em outro portal" e exibindo o mesmo nome. Slug unificado no
  // backend e as 16 linhas migradas; a chave antiga não casa mais com nada.
  almn: { label: 'ALMN', color: 'text-slate-400' },
  plnp: { label: 'PLNP', color: 'text-slate-400' },
  nvld: { label: 'NVLD', color: 'text-slate-400' },
};

export const MURAL_PORTAL_KEYS = new Set(['pi', 'trp', 'dm', 'dmsc', 'pb', 'cepe', 'dioe', 'dio', 'iomat', 'ioepa', 'doem', 'dem', 'sm', 'bsit', 'sesc']);

export function isMuralSource(item: {
  portalOrigem?: string | null;
  linkOrigem?: string | null;
  pncpId?: string | null;
  orgaoCnpj?: string | null;
  numeroCompra?: string | null;
  dataEncerramento?: string | Date | null;
}): boolean {
  const key = (item.portalOrigem || '').toLowerCase();
  if (MURAL_PORTAL_KEYS.has(key)) return true;
  const link = (item.linkOrigem || '').toLowerCase();
  if (/diario|mural|transparencia|\/dom\b|dioe?\.|ioes\.|tce\.[a-z]{2}\.gov/.test(link)) return true;

  // Reconhecimento por FORMA, não por lista. Manter uma lista de slugs é perder
  // a corrida: o AlertaLicitação inventa um prefixo novo por município, e a cada
  // um deles a tela volta a mostrar o slug cru como se fosse portal de disputa
  // (avndv, gtln, jrml apareceram assim em 03/09/2026).
  //
  // O que caracteriza o aviso de mural é a ausência combinada: veio do agregador
  // sem chave PNCP, sem CNPJ do órgão, sem número de edital e sem prazo
  // publicado. Item de portal de verdade tem pelo menos o prazo — o BNC #11777 e
  // os BLL #12605/#12834 não têm chave PNCP e nem por isso são mural.
  const semIdentificacao = !item.orgaoCnpj && !item.numeroCompra && !item.dataEncerramento;
  return semIdentificacao && String(item.pncpId || '').startsWith('AL-');
}

export function getDispensaBadge(item: { instrumentoConvocatorio?: string | null; portalOrigem?: string | null }): { label: string; color: string; title: string } | null {
  const instr = (item.instrumentoConvocatorio || '').toLowerCase();
  if (!instr) return null;
  if (instr.includes('ato que autoriza')) {
    return { label: '🚫 Já contratada', color: 'bg-red-500/15 text-red-300 border-red-600/30', title: 'Ato que autoriza a Contratação Direta — fornecedor já decidido' };
  }
  if (instr.includes('aviso') && instr.includes('contrata')) {
    const portal = (item.portalOrigem || '').toLowerCase();
    const hasPortalReal = portal && portal !== 'pncp' && PORTAL_MAP[portal];
    if (!hasPortalReal) {
      return { label: '📧 Dispensa por email', color: 'bg-cyan-500/15 text-cyan-300 border-cyan-600/30', title: 'Aviso de Contratação Direta sem portal eletrônico — envio direto por email (ver o edital)' };
    }
  }
  return null;
}

export const fmt = (v: number | null | undefined) =>
  v != null
    ? 'R$ ' + Number(v).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    : '—';

/**
 * Valor unitário com fidelidade ao número real:
 * - Pra licitações tipo "milheiro de chamada" (R$ 0,075 ou R$ 0,1446), o
 *   portal aceita até 4 casas decimais. Arredondar pra 2 perde informação
 *   e dá impressão de proposta diferente do que foi enviado.
 * - Pra valores "redondos" (>=1 sem frac>2 casas), mantém 2 casas pra não
 *   poluir com zeros (ex: R$ 21.140,00 e não R$ 21.140,0000).
 * Detecta se o número tem >2 casas decimais significativas via comparação
 * (round(n*100) !== round(n*10000)).
 */
export const fmtUnitPrice = (v: number | null | undefined): string => {
  if (v == null) return '—';
  const n = Number(v);
  const has3PlusDecimals = Math.round(n * 100) !== Math.round(n * 10000);
  const digits = has3PlusDecimals ? 4 : 2;
  return 'R$ ' + n.toLocaleString('pt-BR', {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });
};

export const getStatusInfo = (status: string) =>
  ITEM_STATUSES.find((s) => s.value === status) || ITEM_STATUSES[0];


/**
 * Link pra ABRIR o item no portal OPERACIONAL (BLL/BNC/PCP/Licitar Digital/…):
 * qualquer linkOrigem que NÃO seja PNCP nem ComprasNet conta — é onde de fato
 * se opera/cadastra. ComprasNet mantém o fluxo próprio (página pública cnet).
 * Retorna null se não houver link operacional → o chamador cai no padrão
 * (cnet/pncp) ou tenta resolver via POST items/:id/resolve-portal-link.
 */
export function operationalPortalUrl(item: any): string | null {
  const lo = item?.linkOrigem;
  if (!lo) return null;
  const full = /^https?:\/\//.test(lo) ? lo : 'https://' + lo;
  const po = String(item?.portalOrigem || '').toLowerCase();
  if (po === 'comprasnet') return null;
  let host = '';
  try { host = new URL(full).hostname.toLowerCase(); } catch { return null; }
  if (!host || host.includes('pncp.gov.br') || host.includes('compras.gov.br')) return null;
  return full;
}

/**
 * Link ÚNICO pra abrir a licitação no portal, na ordem que o operador espera:
 * portal operacional (BLL/BNC/PCP/Licitar…) → página pública do ComprasNet →
 * PNCP → `linkOrigem` cru.
 *
 * Existia copiado em três telas (Cadastrar, Monitorar e o workspace) e faltava
 * justamente no card da tela Disputar, que é onde o operador está quando
 * precisa abrir o portal — no minuto da sessão. Agora tem um dono só.
 *
 * ⛔ `linkOrigem` do AlertaLicitação chega como " " (espaço) em parte dos
 * itens, e espaço é truthy: o fallback cru precisa de `trim()` e de um ponto
 * no meio, senão devolve um href que abre uma aba em branco.
 */
export function portalLinkUrl(item: any): { url: string; title: string } | null {
  if (!item) return null;
  const operacional = operationalPortalUrl(item);
  if (operacional) return { url: operacional, title: 'Abrir no portal' };
  const cnet = comprasnetPublicUrl(item);
  if (cnet) return { url: cnet, title: 'Abrir no ComprasNet (página pública)' };
  const pncp = pncpPublicUrl(item?.pncpId);
  if (pncp) return { url: pncp, title: 'Abrir no PNCP' };
  const cru = String(item?.linkOrigem || '').trim();
  if (!cru || !cru.includes('.')) return null;
  return {
    url: /^https?:\/\//.test(cru) ? cru : 'https://' + cru,
    title: 'Abrir no portal',
  };
}

/**
 * Como o portal chama o agrupamento de itens. BLL e BNC dizem "LOTE"; o
 * ComprasNet e o PNCP dizem "GRUPO".
 *
 * Não é preciosismo de nomenclatura: com a tabela escrevendo "GRUPO" onde o
 * edital do BLL escreve "LOTE", o operador olhou a proposta de Nova Olímpia
 * 021/2026 e concluiu que os 3 lotes não tinham sido capturados — estavam
 * todos lá (18/08/2026).
 */
export const LABEL_AGRUPAMENTO = (portalOrigem?: string | null): string => {
  const p = String(portalOrigem || '').toLowerCase();
  return p === 'bll' || p === 'bnc' || p === 'pcp' ? 'LOTE' : 'GRUPO';
};

/**
 * Chave do PORTAL_MAP a que uma credencial de Acessos pertence, pelo nome.
 *
 * É a mesma heurística que o LicitacoesTab usava inline para montar o "✓" do
 * PortalSelect. Saiu para cá porque o seletor de credencial da Nova Proposta
 * precisa da MESMA resposta: se as duas telas casassem nome com portal de
 * jeitos diferentes, uma diria que o portal tem robô e a outra não.
 */
/**
 * Credenciais cujo nome em Acessos não bate com o rótulo do PORTAL_MAP por
 * nenhuma das regras de semelhança abaixo. A chave é o nome SEM a conta,
 * minúsculo e só com letras e números.
 *
 * "Compras Minas Gerais" contra o rótulo "Compras MG": sem isto a credencial
 * não ganhava o ✓ no seletor do painel e a Nova Proposta de uma licitação de
 * MG abria sem portal.
 */
const APELIDOS_DE_CREDENCIAL: Record<string, string> = {
  comprasminasgerais: 'compras_mg',
};

export function portalKeyForAccount(portalName: string | null | undefined): string | null {
  const name = (portalName || '').toLowerCase();
  if (!name) return null;
  const apelido = APELIDOS_DE_CREDENCIAL[splitAccountName(name).base.replace(/[^a-z0-9]/g, '')];
  if (apelido) return apelido;
  const nameNorm = name.replace(/[^a-z0-9]/g, '');
  for (const [key, map] of Object.entries(PORTAL_MAP)) {
    const label = map.label.toLowerCase();
    if (name === label || name.includes(label) || label.includes(name) || nameNorm.includes(key) || key.includes(nameNorm)) {
      return key;
    }
  }
  return null;
}

/** "Banrisul (Fabio)" → { base: 'Banrisul', conta: 'Fabio' }; sem parênteses, conta é null. */
export function splitAccountName(portalName: string | null | undefined): { base: string; conta: string | null } {
  const raw = (portalName || '').trim();
  const m = raw.match(/^(.*?)\s*\((.+)\)\s*$/);
  if (!m || !m[1]) return { base: raw, conta: null };
  return { base: m[1].trim(), conta: m[2].trim() };
}

export interface PortalAccountGroup {
  /** Nome do portal, sem o sufixo de conta. */
  base: string;
  /** Chave do PORTAL_MAP, quando o nome casa com alguma. */
  key: string | null;
  automacao: boolean;
  contas: Array<{ id: string; conta: string | null }>;
}

/**
 * Agrupa as credenciais de Acessos por portal: "Banrisul (Fabio)", "Banrisul
 * (Mae)" etc. viram UM portal com contas dentro, em vez de itens irmãos numa
 * lista plana. E separa os portais pelo que importa na hora de cadastrar a
 * proposta: se o robô opera ali ou não.
 */
export function groupPortalAccounts(
  portals: Array<{ id: number | string; portalName?: string | null }>,
): { comAutomacao: PortalAccountGroup[]; semAutomacao: PortalAccountGroup[] } {
  const porBase = new Map<string, PortalAccountGroup>();
  for (const p of portals) {
    const { base, conta } = splitAccountName(p.portalName);
    if (!base) continue;
    let g = porBase.get(base.toLowerCase());
    if (!g) {
      // Casa pelo nome SEM a conta: "Portal Compras Públicas (Disputar)" tem
      // de cair no mesmo portal que "Portal Compras Públicas".
      const key = portalKeyForAccount(base);
      g = { base, key, automacao: !!key && PORTAIS_COM_AUTOMACAO.has(key), contas: [] };
      porBase.set(base.toLowerCase(), g);
    }
    g.contas.push({ id: String(p.id), conta });
  }
  const grupos = Array.from(porBase.values());
  for (const g of grupos) {
    // A conta sem sufixo (a principal) vem primeiro; o resto, alfabético.
    g.contas.sort((a, b) => (a.conta === null ? -1 : b.conta === null ? 1 : a.conta.localeCompare(b.conta)));
  }
  grupos.sort((a, b) => a.base.localeCompare(b.base));
  return {
    comAutomacao: grupos.filter((g) => g.automacao),
    semAutomacao: grupos.filter((g) => !g.automacao),
  };
}

/**
 * Qual credencial de Acessos usar para uma licitação de um portal (`source` é
 * a chave do PORTAL_MAP gravada em `portalOrigem`).
 *
 * ⛔ Antes, dois lugares procuravam o CÓDIGO dentro do NOME: `licitar_digital`
 * nunca aparece em "Licitar Digital", então todo portal de código com
 * sublinhado (Licitar Digital, AMM Licita, Compras Amazonas, SISLOG...) abria a
 * Nova Proposta sem portal. E o contrário: `bb` (Licitações-e BB) achava
 * "BBMNET", que é outro portal.
 *
 * Agora casa pela MESMA chave que o seletor usa (`portalKeyForAccount`), ou pelo
 * nome normalizado igual ao código. Com várias contas no portal, prefere a
 * principal (sem sufixo); sem principal, a primeira na ordem recebida.
 */
export function accountIdForSource(
  portals: Array<{ id: number | string; portalName?: string | null }>,
  source: string | null | undefined,
): string {
  if (!source || source === 'pncp' || !Array.isArray(portals)) return '';
  const norm = (s: string) => s.toLowerCase().normalize('NFD').replace(/[^a-z0-9]/g, '');
  const alvo = norm(source);
  const candidatas = portals.filter((p) => {
    const { base } = splitAccountName(p.portalName);
    return portalKeyForAccount(base) === source || norm(base) === alvo;
  });
  if (!candidatas.length) return '';
  const principal = candidatas.find((p) => splitAccountName(p.portalName).conta === null);
  return String((principal || candidatas[0]).id);
}
